/**
 * The horizontal scale both timelines draw on: hours from midnight across a 31-hour day (so an
 * overnight route runs on past 24:00), at a zoomable number of pixels per hour.
 */
import { addMinutes, differenceInMinutes, startOfDay } from "date-fns"

export const TIMELINE_START_HOUR = 0
/** 31 hours so an overnight route (00:00 to 07:00 next day) stays on one row. */
export const TIMELINE_END_HOUR = 31
export const TOTAL_HOURS = TIMELINE_END_HOUR - TIMELINE_START_HOUR
/** Pixels per hour at 100%. */
export const DEFAULT_HOUR_WIDTH = 135
export const MIN_HOUR_WIDTH = 45
export const MAX_HOUR_WIDTH = 270
export const ZOOM_STEP = 15
/** Fixed row height, which is what keeps the label column and the track column aligned. */
export const ROW_HEIGHT = 65

/** Where a time sits on a day's timeline, clamped to the drawable range. */
export function timeToPosition(time: Date, day: Date, hourWidth: number): number {
  const start = startOfDay(day)
  start.setHours(TIMELINE_START_HOUR)
  const hoursSinceStart = Math.max(0, Math.min(differenceInMinutes(time, start) / 60, TOTAL_HOURS))
  return hoursSinceStart * hourWidth
}

/** The time at a pixel offset along a day's timeline. */
export function positionToTime(x: number, day: Date, hourWidth: number): Date {
  const start = startOfDay(day)
  start.setHours(TIMELINE_START_HOUR)
  return addMinutes(start, (x / hourWidth) * 60)
}

/** Round a time to the nearest interval within its hour, dropping seconds. */
export function snapToInterval(date: Date, intervalMinutes: number): Date {
  const result = new Date(date)
  result.setMinutes(Math.round(date.getMinutes() / intervalMinutes) * intervalMinutes, 0, 0)
  return result
}

export interface HourMarker {
  hour: number
  label: string
  position: number
  isNextDay: boolean
}

/** One marker per hour, labelled "(+1)" past midnight. */
export function buildHourMarkers(hourWidth: number): HourMarker[] {
  return Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => {
    const hour = TIMELINE_START_HOUR + i
    const isNextDay = hour >= 24
    const clock = `${(isNextDay ? hour - 24 : hour).toString().padStart(2, "0")}:00`
    return { hour, label: isNextDay ? `${clock} (+1)` : clock, position: i * hourWidth, isNextDay }
  })
}

export function timelineWidthFor(hourWidth: number): number {
  return (TOTAL_HOURS + 1) * hourWidth
}
